/**
 * ═══════════════════════════════════════════════════════════════════════
 * useRealtimeEvent — Selective Event Subscription Hook
 * ═══════════════════════════════════════════════════════════════════════
 *
 * Subscribes a component to specific realtime event types.
 * Only the subscribing component re-renders — no global re-renders.
 *
 * Usage:
 *   // Listen for a single event type
 *   useRealtimeEvent('VISIT_CREATED', (event) => {
 *     showToast(`New visit: ${event.payload.visit.patient_name}`);
 *   });
 *
 *   // Listen for multiple event types
 *   useRealtimeEvent(
 *     ['VISIT_STATUS_CHANGED', 'QUEUE_STARTED', 'QUEUE_SKIPPED'],
 *     (event) => { refetchMyQueue(); }
 *   );
 *
 *   // Get last event data for rendering
 *   const lastEvent = useRealtimeEvent('VISIT_CREATED');
 */

import { useEffect, useRef, useState } from 'react';
import eventBus from '../services/realtime/EventBus';

/**
 * @param {string|string[]} eventTypes - Event type(s) to subscribe to
 * @param {Function}        [handler]  - Optional callback. If omitted, returns last event.
 * @returns {{ lastEvent: Object|null, eventCount: number }}
 */
export function useRealtimeEvent(eventTypes, handler) {
  const [lastEvent, setLastEvent] = useState(null);
  const [eventCount, setEventCount] = useState(0);
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  // Normalize to array
  const types = Array.isArray(eventTypes) ? eventTypes : [eventTypes];
  // Stable key for dependency
  const typesKey = types.sort().join(',');

  useEffect(() => {
    const unsubscribers = types.map((type) =>
      eventBus.on(type, (event) => {
        // If a handler is provided, call it
        if (handlerRef.current) {
          handlerRef.current(event);
        }

        // Always track last event for optional rendering
        setLastEvent(event);
        setEventCount((c) => c + 1);
      })
    );

    return () => {
      unsubscribers.forEach((unsub) => unsub());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typesKey]);

  return { lastEvent, eventCount };
}

/**
 * useRealtimeCallback — Fire-and-forget event listener.
 * No state, no re-renders. Just runs your callback.
 *
 * Usage:
 *   useRealtimeCallback('VISIT_CREATED', (event) => {
 *     playNotificationSound();
 *   });
 */
export function useRealtimeCallback(eventTypes, callback) {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  const types = Array.isArray(eventTypes) ? eventTypes : [eventTypes];
  const typesKey = types.sort().join(',');

  useEffect(() => {
    const unsubscribers = types.map((type) =>
      eventBus.on(type, (event) => {
        callbackRef.current?.(event);
      })
    );

    return () => {
      unsubscribers.forEach((unsub) => unsub());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typesKey]);
}

export default useRealtimeEvent;
